import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import upperFirst from 'lodash/upperFirst';
import TextInput from '../TextInput';
import './CreditCardFields.css';

const propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

const defaultProps = {
  className: '',
  disabled: false,
  onChange: () => {},
};

const classes = {
  complete: 'CreditCardFields-input--complete',
  focus: 'CreditCardFields-input--focus',
  invalid: 'CreditCardFields-input--invalid',
};

const style = {
  base: {
    color: '#00345c',
    fontFamily: '"Nunito", Helvetica, sans-serif',
    fontSize: '16px',
    fontSmoothing: 'antialiased',
    '::placeholder': {
      color: '#dadfe9',
    },
  },
  invalid: {
    color: '#cc1236',
  },
};

const cardBrandToPfClass = {
  visa: 'pf-visa',
  mastercard: 'pf-mastercard',
  amex: 'pf-american-express',
  discover: 'pf-discover',
  unknown: 'pf-credit-card',
};

class CreditCardFields extends Component {
  state = {
    cardCvc: false,
    cardExpiry: false,
    cardNumber: false,
    brandClasses: [],
    cardName: '',
    isCardNameComplete: false,
    isCardCvcInvalid: false,
    isCardExpiryInvalid: false,
    isCardNumberInvalid: false,
  };

  componentDidMount() {
    this.init();
    this.setBrandIcon('unknown');
  }

  init() {
    if (!window.Stripe) {
      console.error('Cannot init CreditCardFields without Stripe.');
      return false;
    }

    const elements = window.stripe.elements({
      fonts: [
        {
          // cssSrc: 'https://fonts.googleapis.com/css?family=Nunito',
        },
      ],
    });
    /**
     * CARD NUMBER
     */
    this.cardNumberElement = elements.create('cardNumber', {
      classes,
      style,
      placeholder: '0000 0000 0000 0000',
    });
    this.cardNumberElement.mount(this.cardNumberMountNode);
    this.cardNumberElement.on('change', this.handleChange);
    /**
     * CARD EXPIRY
     */
    this.cardExpiryElement = elements.create('cardExpiry', {
      classes,
      style,
    });
    this.cardExpiryElement.mount(this.cardExpiryMountNode);
    this.cardExpiryElement.on('change', this.handleChange);
    /**
     * CARD CVC
     */
    this.cardCvcElement = elements.create('cardCvc', {
      classes,
      style,
      placeholder: '000',
    });
    this.cardCvcElement.mount(this.cardCvcMountNode);
    this.cardCvcElement.on('change', this.handleChange);
  }

  isFieldInvalid = (e) => {
    this.setState({
      [`is${upperFirst(e.elementType)}Invalid`]: !!e.error,
    });
  };

  handleChange = (e) => {
    if (e.elementType) {
      this.isFieldInvalid(e);
      if (e.brand) {
        this.setBrandIcon(e.brand, e.complete);
      }
      if (e.elementType) {
        this.setState(
          {
            [e.elementType]: e.complete,
          },
          () => {
            this.props.onChange({
              isComplete:
                this.state.cardCvc &&
                this.state.cardExpiry &&
                this.state.cardNumber &&
                this.state.isCardNameComplete,
            });
          }
        );
      }
    } else if (e.target.value !== '') {
      this.setState(
        {
          isCardNameComplete: true,
          cardName: e.target.value,
        },
        () => {
          this.props.onChange({
            isComplete:
              this.state.cardCvc &&
              this.state.cardExpiry &&
              this.state.cardNumber &&
              this.state.isCardNameComplete,
          });
        }
      );
    } else {
      this.setState(
        {
          isCardNameComplete: false,
          cardName: e.target.value,
        },
        () => {
          this.props.onChange({
            isComplete:
              this.state.cardCvc &&
              this.state.cardExpiry &&
              this.state.cardNumber &&
              this.state.isCardNameComplete,
          });
        }
      );
    }
  };

  setBrandIcon(brand, isComplete) {
    let pfClass = 'pf-credit-card';
    if (brand in cardBrandToPfClass) {
      pfClass = cardBrandToPfClass[brand];
    }
    if (isComplete) {
      this.setState({
        brandClasses: ['pf', pfClass, 'CreditCardFields-brand-i-complete'],
      });
    } else {
      this.setState({
        brandClasses: ['pf', pfClass],
      });
    }
  }

  reset() {
    this.cardNumberElement.unmount();
    this.cardExpiryElement.unmount();
    this.cardCvcElement.unmount();
    this.setState({
      cardName: '',
    });
    this.init();
  }

  get cardName() {
    return this.state.cardName;
  }

  get element() {
    return this.cardNumberElement;
  }

  render() {
    const { className, disabled } = this.props;

    return (
      <div
        className={classnames('CreditCardFields', className, {
          'is-disabled': disabled,
        })}
      >
        <label className="CreditCardFields-label" htmlFor="cardName">
          Cardholder Name
          <TextInput
            name="cardName"
            id="cardName"
            required
            type="text"
            onChange={this.handleChange}
            value={this.state.cardName}
          />
        </label>
        <label
          className={classnames('CreditCardFields-label', {
            'is-invalid': this.state.isCardNumberInvalid,
          })}
          htmlFor="cardNumberElement"
        >
          <span className="CreditCardFields-label-span">Card Number</span>
          <div
            ref={(c) => (this.cardNumberMountNode = c)}
            className="CreditCardFields-input"
            id="cardNumberElement"
          />
          <span className="CreditCardFields-label-span CreditCardFields-brand">
            <i
              className={classnames(
                'CreditCardFields-brand-i',
                this.state.brandClasses
              )}
              id="brand-icon"
            />
          </span>
        </label>

        <div className="CreditCardFields-inputRow--desktop">
          <div className="CreditCardFields-inputRow-item--desktop">
            <label
              className={classnames('CreditCardFields-label', {
                'is-invalid': this.state.isCardExpiryInvalid,
              })}
              htmlFor="cardExpiryElement"
            >
              <span className="CreditCardFields-label-span">
                Expiration Date
              </span>
              <div
                ref={(c) => (this.cardExpiryMountNode = c)}
                className="CreditCardFields-input"
                id="cardExpiryElement"
              />
            </label>
          </div>
          <div className="CreditCardFields-inputRow-item--desktop">
            <label
              className={classnames('CreditCardFields-label', {
                'is-invalid': this.state.isCardCvcInvalid,
              })}
              htmlFor="cardCvcElement"
            >
              <span className="CreditCardFields-label-span">
                Security Code (CVC)
              </span>
              <div
                ref={(c) => (this.cardCvcMountNode = c)}
                className="CreditCardFields-input"
                id="cardCvcElement"
              />
            </label>
          </div>
        </div>
      </div>
    );
  }
}

CreditCardFields.propTypes = propTypes;
CreditCardFields.defaultProps = defaultProps;

export default CreditCardFields;
